import {
  Card,
  CardContent,
  Badge,
  CardTitle,
  IconBadge,
  type IconBadgeProps,
} from '@monksflow/monks-ui';
import {
  BrainIcon,
  CompassIcon,
  SparklesIcon,
  TargetIcon,
  TriangleAlertIcon,
  type LucideIcon,
} from 'lucide-react';
import type { PersonaDetailsSectionProps } from './PersonaDetailsSection.types';
import { SectionHeader } from '@/shared/ui/SectionHeader';
import { useTranslation } from 'react-i18next';

interface DetailCardProps {
  title: string;
  icon: LucideIcon;
  /* Neutral unless the card's content carries a direction: what the audience wants reads
     positive, what stands in their way reads destructive. */
  iconVariant?: IconBadgeProps['variant'];
  items: string[];
}

const DetailCard = ({ title, icon: Icon, iconVariant = 'secondary', items }: DetailCardProps) => {
  if (items.length === 0) return null;

  return (
    <Card variant="outline" className="shadow-none px-6 border-border-light">
      <CardTitle className="flex flex-col items-start gap-4 type-heading-sm">
        <IconBadge variant={iconVariant}>
          <Icon />
        </IconBadge>
        {title}
      </CardTitle>
      <CardContent className="flex flex-col gap-4">
        <ul className="list-disc list-outside flex flex-col gap-2 type-body-md text-foreground-alt marker:text-muted-foreground">
          {items.map((item, index) => (
            <li key={`${item}-${index}`}>{item}</li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

interface PsychographicsCardProps {
  lifestyle: string;
  aspirations: string[];
  behaviors: string[];
}

const PsychographicsCard = ({ lifestyle, aspirations, behaviors }: PsychographicsCardProps) => {
  const { t } = useTranslation('audiences');
  return (
    <Card variant="outline" className="shadow-none border-border-light md:col-span-2">
      <CardTitle className="flex flex-col items-start gap-4 px-6 type-heading-sm">
        <IconBadge variant="secondary">
          <BrainIcon />
        </IconBadge>
        {t('audienceFlowWidget.personaDetailsSection.psychographics')}
      </CardTitle>
      <CardContent className="flex flex-col gap-5">
        {lifestyle && (
          <div className="flex flex-col gap-2">
            <span className="type-heading-xs text-foreground">
              {t('audienceFlowWidget.personaDetailsSection.lifestyle')}
            </span>
            <p className="type-body-md text-foreground-subtle">{lifestyle}</p>
          </div>
        )}

        {aspirations.length > 0 && (
          <div className="flex flex-col gap-2">
            <span className="type-heading-xs text-foreground">
              {t('audienceFlowWidget.personaDetailsSection.aspirations')}
            </span>
            <div className="flex flex-wrap gap-2">
              {aspirations.map((aspiration, index) => (
                <Badge key={`${aspiration}-${index}`} variant="secondary">
                  {aspiration}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {behaviors.length > 0 && (
          <div className="flex flex-col gap-2">
            <span className="type-heading-xs text-foreground">
              {t('audienceFlowWidget.personaDetailsSection.behaviors')}
            </span>
            <ul className="list-disc list-outside flex flex-col gap-2 type-body-md text-foreground-subtle marker:text-muted-foreground px-6">
              {behaviors.map((behavior, index) => (
                <li key={`${behavior}-${index}`}>{behavior}</li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const PersonaDetailsSection = ({ persona }: PersonaDetailsSectionProps) => {
  const { t } = useTranslation('audiences');

  const psychographics = persona.psychometricsData?.data.psychographics;
  const cfc = persona.psychometricsData?.data.CFC;

  const coreValues = psychographics?.coreValues || [];
  const interests = persona.demographicsData?.data.interests || [];
  const needsAndMotivations = psychographics?.needsAndMotivations || [];
  const painPoints = psychographics?.painPoints || cfc?.pains || [];

  const lifestyle = psychographics?.lifestyle || '';
  const aspirations = psychographics?.aspirations || [];
  const behaviors = psychographics?.behaviors || [];

  const hasMainDetails =
    coreValues.length > 0 ||
    interests.length > 0 ||
    needsAndMotivations.length > 0 ||
    painPoints.length > 0;

  const hasPsychographics = lifestyle || aspirations.length > 0 || behaviors.length > 0;

  if (!hasMainDetails && !hasPsychographics) {
    return null;
  }

  return (
    <section className="flex flex-col gap-6">
      <SectionHeader title={t('audienceFlowWidget.personaDetailsSection.personaDetails')} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <DetailCard
          title={t('audienceFlowWidget.personaDetailsSection.coreValues')}
          icon={CompassIcon}
          items={coreValues}
        />
        <DetailCard
          title={t('audienceFlowWidget.personaDetailsSection.keyInterests')}
          icon={SparklesIcon}
          items={interests}
        />
        <DetailCard
          title={t('audienceFlowWidget.personaDetailsSection.needsMotivations')}
          icon={TargetIcon}
          iconVariant="positive"
          items={needsAndMotivations}
        />
        <DetailCard
          title={t('audienceFlowWidget.personaDetailsSection.painPoints')}
          icon={TriangleAlertIcon}
          iconVariant="destructive"
          items={painPoints}
        />
        {hasPsychographics && (
          <PsychographicsCard
            lifestyle={lifestyle}
            aspirations={aspirations}
            behaviors={behaviors}
          />
        )}
      </div>
    </section>
  );
};
